//@ts-nocheck
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useMySubscriptionQuery,
  SubscriptionStatus,
  Role,
} from "../gql/generated";
import { useAtom } from "jotai";
import { authAtom } from "@src/atoms";
import { useUser } from "./UserContext";

export interface PlanPermissions {
  name: string;
  priceMonthly: number;
  description?: string | null;
  companyPages?: number | null;
  connectionSendingLimits?: number | null;
  serviceListings?: number | null;
  groupCreation?: number | null;
  actionButtonOnProfile: boolean;
  liveStreaming: boolean;
  accessAIChatbot: boolean;
}

/** Computed by the backend: NONE | TRIAL | ACTIVE | GRACE | RESTRICTED. */
export type AccessState = "NONE" | "TRIAL" | "ACTIVE" | "GRACE" | "RESTRICTED";

export interface SubscriptionData {
  id: string;
  planId: string;
  status: SubscriptionStatus;
  currentPeriodStart?: string | null;
  currentPeriodEnd?: string | null;
  planLimitsPeriodStart?: string | null;
  trialEndsAt?: string | null;
  graceEndsAt?: string | null;
  accessState?: AccessState | null;
  cancelAtPeriodEnd: boolean;
  plan: PlanPermissions & { id: string };
  planSnapshot?: PlanPermissions | null;
}

export type PlanFeature = keyof Pick<
  PlanPermissions,
  | "companyPages"
  | "connectionSendingLimits"
  | "serviceListings"
  | "groupCreation"
  | "actionButtonOnProfile"
  | "liveStreaming"
  | "accessAIChatbot"
>;

interface ISubscriptionContextType {
  subscription: SubscriptionData | null;
  loading: boolean;
  setSubscriptionFromLogin: (sub: SubscriptionData) => void;
  refetchSubscription: () => void;
  hasActiveSubscription: boolean;
  /** Free trial running (full plan access, charged when it ends). */
  isTrial: boolean;
  /** Last payment failed: full access for a few more days while we retry. */
  isInGrace: boolean;
  /** Grace period ended unpaid: only the plans page works until the partner pays. */
  isRestricted: boolean;
  canAccess: (feature: PlanFeature) => boolean;
  getLimit: (feature: PlanFeature) => number | null;
  planName: string | null;
}

const SubscriptionContext = createContext<ISubscriptionContextType | undefined>(
  undefined,
);

const STORAGE_KEY = "dentment_subscription";

function getEffectivePlan(sub: SubscriptionData): PlanPermissions {
  return sub.planSnapshot ?? sub.plan;
}

function isActiveSubscriptionStatus(
  status?: SubscriptionStatus | string | null,
): boolean {
  return (
    status === SubscriptionStatus.Active ||
    status === SubscriptionStatus.Trialing ||
    status === "ACTIVE" ||
    status === "TRIALING"
  );
}

/**
 * Paid access as decided by the backend (accessState). Falls back to the raw status for
 * responses that predate accessState; PAST_DUE is only trusted through accessState (GRACE).
 */
function hasPaidAccess(sub?: SubscriptionData | null): boolean {
  if (!sub) return false;
  if (sub.accessState) {
    return (
      sub.accessState === "ACTIVE" ||
      sub.accessState === "TRIAL" ||
      sub.accessState === "GRACE"
    );
  }
  return isActiveSubscriptionStatus(sub.status);
}

type GqlSubscriptionLike = {
  id: string;
  planId: string;
  status: SubscriptionStatus;
  currentPeriodStart?: string | null;
  currentPeriodEnd?: string | null;
  planLimitsPeriodStart?: string | null;
  trialEndsAt?: string | null;
  graceEndsAt?: string | null;
  accessState?: string | null;
  cancelAtPeriodEnd: boolean;
  planSnapshot?: PlanPermissions | null;
  plan: {
    id: string;
    name: string;
    priceMonthly: number;
    description?: string | null;
    companyPages?: number | null;
    connectionSendingLimits?: number | null;
    serviceListings?: number | null;
    groupCreation?: number | null;
    actionButtonOnProfile?: boolean;
    liveStreaming?: boolean;
    accessAIChatbot?: boolean;
  };
};

export function mapGqlSubscriptionToData(
  raw: GqlSubscriptionLike,
): SubscriptionData {
  const snapshot = raw.planSnapshot as PlanPermissions | null | undefined;

  return {
    id: raw.id,
    planId: raw.planId,
    status: raw.status,
    currentPeriodStart: raw.currentPeriodStart,
    currentPeriodEnd: raw.currentPeriodEnd,
    planLimitsPeriodStart: raw.planLimitsPeriodStart,
    trialEndsAt: raw.trialEndsAt ?? null,
    graceEndsAt: raw.graceEndsAt ?? null,
    accessState: (raw.accessState as AccessState | null | undefined) ?? null,
    cancelAtPeriodEnd: raw.cancelAtPeriodEnd,
    plan: {
      id: raw.plan.id,
      name: raw.plan.name,
      priceMonthly: raw.plan.priceMonthly,
      description: raw.plan.description,
      companyPages: raw.plan.companyPages ?? null,
      connectionSendingLimits: raw.plan.connectionSendingLimits ?? null,
      serviceListings: raw.plan.serviceListings ?? null,
      groupCreation: raw.plan.groupCreation ?? null,
      liveStreaming: raw.plan.liveStreaming ?? false,
      accessAIChatbot: raw.plan.accessAIChatbot ?? false,
      actionButtonOnProfile: raw.plan.actionButtonOnProfile ?? false,
    },
    planSnapshot: snapshot ?? null,
  };
}

function persistSubscription(sub: SubscriptionData | null) {
  if (sub) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sub));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

const NUMERIC_LIMIT_FIELDS: (keyof PlanPermissions)[] = [
  "companyPages",
  "connectionSendingLimits",
  "serviceListings",
  "groupCreation",
];

const BOOLEAN_FEATURE_FIELDS: (keyof PlanPermissions)[] = [
  "actionButtonOnProfile",
  "liveStreaming",
  "accessAIChatbot",
];

function planPermissionsEqual(
  a: PlanPermissions,
  b: PlanPermissions,
): boolean {
  if (a.name !== b.name) return false;
  for (const field of NUMERIC_LIMIT_FIELDS) {
    if (a[field] !== b[field]) return false;
  }
  for (const field of BOOLEAN_FEATURE_FIELDS) {
    if (!!a[field] !== !!b[field]) return false;
  }
  return true;
}

function isSameSubscription(
  a: SubscriptionData | null | undefined,
  b: SubscriptionData | null | undefined,
): boolean {
  if (!a || !b) return false;
  const metadataMatches =
    a.id === b.id &&
    a.planId === b.planId &&
    a.status === b.status &&
    a.cancelAtPeriodEnd === b.cancelAtPeriodEnd &&
    a.currentPeriodEnd === b.currentPeriodEnd &&
    a.accessState === b.accessState &&
    a.trialEndsAt === b.trialEndsAt &&
    a.graceEndsAt === b.graceEndsAt;
  if (!metadataMatches) return false;
  return planPermissionsEqual(getEffectivePlan(a), getEffectivePlan(b));
}

export const SubscriptionProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [auth] = useAtom(authAtom);
  const { user: contextUser, loading: userLoading } = useUser();
  const userRole = auth.user?.role ?? contextUser?.role;
  const isIndustryPartner = userRole === Role.IndustryPartners;
  const isAuthenticated = auth.isAuthenticated === true;

  const [subscription, setSubscription] = useState<SubscriptionData | null>(
    () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : null;
      } catch {
        return null;
      }
    },
  );

  const subscriptionQuerySkipped = !isAuthenticated || !isIndustryPartner;

  const {
    data,
    loading,
    called,
    refetch: refetchSubscription,
  } = useMySubscriptionQuery({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    skip: subscriptionQuerySkipped,
  });

  const subscriptionFromQuery = useMemo(
    () =>
      data?.mySubscription
        ? mapGqlSubscriptionToData(data.mySubscription)
        : null,
    [data?.mySubscription],
  );

  const rolePending = isAuthenticated && !userRole && userLoading;
  // Don't block the UI when login/localStorage already gave us a subscription —
  // background refetch can update quietly.
  const hasKnownSubscription =
    Boolean(subscriptionFromQuery) || Boolean(subscription);
  const subscriptionLoading =
    !subscriptionQuerySkipped &&
    !hasKnownSubscription &&
    (rolePending || !called || loading);

  // Prefer live query data; fall back to login/localStorage cache.
  const effectiveSubscription = subscriptionFromQuery ?? subscription;

  useEffect(() => {
    if (subscriptionQuerySkipped) return;
    if (!called || loading) return;

    if (subscriptionFromQuery) {
      setSubscription((prev) =>
        isSameSubscription(prev, subscriptionFromQuery)
          ? prev
          : subscriptionFromQuery,
      );
      persistSubscription(subscriptionFromQuery);
    } else if (data !== undefined) {
      setSubscription((prev) => (prev === null ? prev : null));
      persistSubscription(null);
    }
  }, [
    subscriptionFromQuery,
    called,
    loading,
    data,
    subscriptionQuerySkipped,
  ]);

  useEffect(() => {
    if (!isAuthenticated) {
      setSubscription(null);
      persistSubscription(null);
    }
  }, [isAuthenticated]);

  const setSubscriptionFromLogin = (sub: SubscriptionData) => {
    setSubscription(sub);
    persistSubscription(sub);
  };

  const hasActiveSubscription = hasPaidAccess(effectiveSubscription);
  const accessState = effectiveSubscription?.accessState ?? null;
  const isTrial = accessState === "TRIAL";
  const isInGrace = accessState === "GRACE";
  const isRestricted = accessState === "RESTRICTED";

  const canAccess = (feature: PlanFeature): boolean => {
    if (!effectiveSubscription || !hasActiveSubscription) return false;
    const effectivePlan = getEffectivePlan(effectiveSubscription);
    const value = effectivePlan[feature];
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value > 0;
    return value === null;
  };

  const getLimit = (feature: PlanFeature): number | null => {
    if (!effectiveSubscription || !hasActiveSubscription) return null;
    const effectivePlan = getEffectivePlan(effectiveSubscription);
    const value = effectivePlan[feature];
    if (typeof value === "number") return value;
    return null;
  };

  const planName = effectiveSubscription
    ? getEffectivePlan(effectiveSubscription).name
    : null;

  return (
    <SubscriptionContext.Provider
      value={{
        subscription: effectiveSubscription,
        loading: subscriptionLoading,
        setSubscriptionFromLogin,
        refetchSubscription,
        hasActiveSubscription,
        isTrial,
        isInGrace,
        isRestricted,
        canAccess,
        getLimit,
        planName,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx)
    throw new Error(
      "useSubscription must be used within a SubscriptionProvider",
    );
  return ctx;
};
